import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLang } from "@/lib/i18n.jsx";
import { COUNTRIES } from "@/lib/countries";
import { marketingHref } from "@/lib/localePath";
import api, { saveAuth, errMsg } from "@/lib/api";
import { isNativeApp, nativePlatform } from "@/lib/platform";
import { openAuthWindow } from "@/lib/nativeApp";
import { appleSignIn, appleUnavailableReason, APPLE_SIGNIN_ENABLED } from "@/lib/appleAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import PricingPreview from "@/components/PricingPreview";
import CommissionTable from "@/components/CommissionTable";
import { Chrome, Apple, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const initMode = params.get("mode") === "login" ? "login"
    : params.get("mode") === "staff" ? "staff" : "register";
  const [mode, setMode] = useState(initMode);
  // Dove tornare dopo il login. Le pagine che richiedono un account
  // (/account, /sync, /rivendica...) mandano qui con `?returnTo=`, altrimenti
  // il proprietario arriva sul form della rivendicazione, si iscrive, e
  // viene buttato in dashboard: perde la scheda che aveva gia' trovato e deve
  // cercarla da capo.
  //
  // Accettiamo solo un percorso interno: un `returnTo` che punta a un altro
  // sito trasformerebbe il login in un redirect aperto, cioe' un vettore di
  // phishing su un form che chiede email e password.
  const rawReturnTo = params.get("returnTo") || "";
  const returnTo = /^\/(?!\/)/.test(rawReturnTo) ? rawReturnTo : "/dashboard";
  // Link d'invito: `?mode=staff&email=<indirizzo>`. L'email è la chiave con cui
  // il backend collega l'account al ruolo, quindi prefillarla vale la pena —
  // ma resta modificabile: il link può essere inoltrato, e chi lo apre deve
  // poter correggere un indirizzo sbagliato invece di creare un account che
  // non riceverà mai il ruolo.
  const invitedEmail = (params.get("email") || "").trim();
  const [email, setEmail] = useState(mode === "staff" ? invitedEmail : "");
  const [password, setPassword] = useState("");
  const [structureName, setStructureName] = useState("");
  const [staffName, setStaffName] = useState("");
  const [cin, setCin] = useState("");
  // Il paese dell'immobile, non la lingua dell'interfaccia: una casa in Francia
  // ha obblighi francesi anche se l'host legge l'italiano. È lui a decidere se
  // il CIN serve (solo in Italia) e quali identificativi chiedere.
  const [country, setCountry] = useState("IT");
  const [staffRole, setStaffRole] = useState("general_manager");
  const [affAccepted, setAffAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLang();
  const nav = useNavigate();
  const isNative = isNativeApp();
  //: Apple nativo esiste **solo su iOS**: `ASAuthorizationAppleIDProvider` non
  //: c'è su Android, e il plugin `@capacitor-community/apple-sign-in` non ha
  //: un'implementazione per quella piattaforma. Un pulsante visibile ma
  //: destinato a fallire è peggio di un pulsante assente.
  const isIOS = isNative && nativePlatform() === "ios";
  //: L'interruttore `APPLE_SIGNIN_ENABLED` riguarda **il Services ID web**,
  //: che non esiste ancora; nell'app iOS il login passa dal bundle id e dal
  //: plugin nativo, quindi non c'entra. Tenerlo spento anche lì toglierebbe
  //: «Sign in with Apple» proprio dall'unica piattaforma che lo pretende
  //: (App Store, linea guida 4.8, con Google presente fra i login sociali).
  const appleAvailable = isIOS || APPLE_SIGNIN_ENABLED;
  // Cosa è configurato **sul server** (GET /api/auth/providers). L'accesso con
  // Apple dal browser non si può provare a vuoto: senza chiavi sul backend la
  // popup si aprirebbe, l'utente autorizzerebbe, e la chiamata successiva
  // risponderebbe 503. Meglio dirlo prima, con la causa vera.
  //   `null` = non lo sappiamo ancora: si presume disponibile, perché un errore
  //   di rete non deve nascondere un accesso che funziona.
  const [providers, setProviders] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get("/auth/providers")
      .then((r) => { if (vivo) setProviders(r.data || {}); })
      .catch(() => { /* si prova comunque: il messaggio arriverà dal server */ });
    return () => { vivo = false; };
  }, []);

  //: Il messaggio quando il server non ha le chiavi. Non è un guasto della
  //: piattaforma ed è importante dirlo: senza, chi legge crede che il sito sia
  //: rotto e riprova all'infinito, mentre è solo una chiave da configurare.
  const googleNonConfigurato = () => toast.error(t("auth.google_not_configured"));

  const googleLogin = async () => {
    // Come per Apple, la disponibilità si guarda **prima**. `GET
    // /auth/providers` esiste apposta: senza questo controllo la pagina
    // chiamava comunque `/auth/google/url`, riceveva il 503 e lo mostrava
    // grezzo — sembra un guasto, invece è una chiave non configurata.
    if (providers?.google?.enabled === false) {
      googleNonConfigurato();
      return;
    }
    // `to` e `flusso` finiscono dentro lo stato firmato che il backend genera:
    // il redirect di Google li riporta indietro senza dover passare da
    // sessionStorage, e il server sa che questo login parte dall'host — quindi
    // un'email sconosciuta non gli crea un account ospite.
    sessionStorage.setItem("hh_google_flow", "host");
    try {
      // `native: 1` dice al server che il consenso si apre nel browser di
      // sistema: la sessione deve tornare all'app con un codice monouso
      // (`hostaly://auth/google?handoff=…`), non con un cookie che il WebView
      // non vedrebbe mai.
      const r = await api.get("/auth/google/url", {
        params: { to: returnTo, flusso: "host", ...(isNative ? { native: 1 } : {}) },
      });
      const url = typeof r?.data?.url === "string" ? r.data.url : "";
      if (!url) {
        // Non è JSON: capita sugli hosting che servono solo il frontend e
        // rispondono con `index.html` a ogni percorso. Senza questo controllo
        // `window.location.href = undefined` porta a /undefined.
        googleNonConfigurato();
        return;
      }
      // Nel guscio apre il browser di sistema (Google rifiuta le WebView
      // incorporate); sul web naviga la pagina come sempre.
      await openAuthWindow(url);
    } catch (e) {
      toast.error(errMsg(e, t("auth.google_unavailable")));
    }
  };

  const appleLogin = async () => {
    // `flusso: "host"` dice al backend da quale porta parte il login, come
    // fa gia Google: un'email che non conosce non diventa un account ospite.
    // Senza, un proprietario che si registrava con Apple finiva nel portale
    // ospiti e non poteva piu gestire la struttura.
    //
    // La disponibilità si guarda **prima** di aprire il foglio di Apple: le due
    // cause (chiavi assenti sul server, indirizzo non https) sono entrambe
    // note qui, e scoprirle dopo l'autorizzazione significa far fare all'utente
    // un lavoro che finisce in un errore.
    // `native: isNative` perché nell'app iOS l'indirizzo del WebView
    // (`capacitor://localhost`) non c'entra con Apple: quel ramo non passa dal
    // browser né da un Return URL. Bloccarlo qui toglierebbe il login Apple
    // proprio dall'app che Apple lo pretende (App Store, 4.8).
    const motivo = appleUnavailableReason({
      configured: providers?.apple?.enabled ?? true,
      origin: window.location.origin,
      lang,
      native: isIOS,
      enabled: appleAvailable,
    });
    if (motivo) {
      toast.error(motivo);
      return;
    }
    try {
      const { identityToken, fullName } = await appleSignIn(lang);
      if (!identityToken) return; // user cancelled
      const r = await api.post("/auth/apple/session", {
        identity_token: identityToken, name: fullName, flusso: "host",
      });
      saveAuth(r.data.token, r.data.user);
      toast.success(t("auth.welcome_back"));
      nav(returnTo);
    } catch (e) {
      toast.error(errMsg(e, t("auth.apple_failed")));
    }
  };

  // Il rientro dall'app (`hostaly://auth/google?handoff=…`) lo gestisce
  // `NativeAuthBridge`, montato sopra il router: legato alla pagina si perdeva
  // quando il guscio veniva riavviato dal deep link (vedi il componente).

  const submit = async (e) => {
    e.preventDefault();
    // Il CIN esiste solo in Italia: chiederlo a una struttura francese
    // significherebbe farle copiare un codice italiano che non le appartiene.
    if (mode === "register" && country === "IT") {
      const clean = cin.trim().toUpperCase().replace(/[\s-]/g, "");
      if (!/^IT[A-Z0-9]{14,20}$/.test(clean)) {
        toast.error(t("auth.cin_invalid"));
        return;
      }
    }
    if (mode === "register" && !affAccepted) {
      toast.error(t("auth.aff_required"));
      return;
    }
    setLoading(true);
    try {
      // Tre destinazioni diverse, non tre varianti dello stesso form: lo staff
      // non ha un CIN e non possiede una struttura, quindi il percorso normale
      // lo porterebbe a creare una seconda struttura fantasma.
      const path = mode === "login" ? "/auth/login"
        : mode === "staff" ? "/auth/register-staff"
        : "/auth/register";
      const body = mode === "login"
        ? { email, password }
        : mode === "staff"
        ? { email, password, name: staffName || null }
        : { email, password, structure_name: structureName, staff_role: staffRole,
            country,
            // Solo l'Italia ha il CIN: per gli altri paesi il numero locale si
            // aggiunge dalla scheda della struttura, dove il registro sa quale
            // identificativo chiedere.
            cin: country === "IT" ? cin.trim().toUpperCase().replace(/[\s-]/g, "") : "",
            // La lingua viaggia con l'iscrizione: la stessa persona da un altro
            // dispositivo deve ritrovare quella scelta, non l'italiano.
            locale: lang };
      const r = await api.post(path, body);
      saveAuth(r.data.token, r.data.user);
      toast.success(mode === "login" ? t("auth.welcome_back") : t("auth.account_created"));
      nav(returnTo);
    } catch (err) {
      toast.error(errMsg(err, "Error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-[420px_1fr] gap-10 items-start">
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">{t("nav.host_dashboard")}</div>
          <h1 className="font-display text-4xl tracking-tight mb-2" data-testid="auth-title">
            {mode === "login" ? t("auth.login")
              : mode === "staff"
              ? t("auth.staff_title")
              : t("auth.register")}
          </h1>
          {mode === "staff" ? (
            <p className="text-sm text-muted-foreground mb-6" data-testid="auth-staff-hint">
              {t("auth.staff_hint")}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mb-6">{t("auth.demo_hint")}</p>
          )}

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          {mode === "staff" && (
            <div>
              <Label htmlFor="staff-name">
                {t("auth.staff_name")}
              </Label>
              <Input id="staff-name" data-testid="input-staff-name"
                     value={staffName} required={false}
                     onChange={(e)=>setStaffName(e.target.value)} />
              <p className="text-xs text-muted-foreground mt-1">
                {t("auth.staff_name_help")}
              </p>
            </div>
          )}
          {mode === "register" && (
            <div>
              <Label htmlFor="structure">{t("auth.structure")}</Label>
              <Input id="structure" data-testid="input-structure" value={structureName} onChange={(e)=>setStructureName(e.target.value)} required />
            </div>
          )}
          {mode === "register" && (
            <div>
              <Label htmlFor="country">{t("auth.country")}</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger id="country" data-testid="input-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c[lang] || c.en}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {t("auth.country_help")}
              </p>
            </div>
          )}
          {mode === "register" && country !== "IT" && (
            <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="cin-not-required">
              {t("auth.cin_not_required")}
            </p>
          )}
          {mode === "register" && country === "IT" && (
            <div>
              <Label htmlFor="cin" className="flex items-center gap-1">
                {t("auth.cin_label")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cin"
                data-testid="input-cin"
                value={cin}
                onChange={(e)=>setCin(e.target.value.toUpperCase())}
                placeholder="IT058091C25010A5B"
                maxLength={25}
                required
                pattern="^IT[A-Za-z0-9 \-]{13,25}$"
                title={t("auth.cin_title")}
                className="uppercase font-mono tracking-wider"
              />
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {t("auth.cin_help")}
              </p>
            </div>
          )}
          {mode === "register" && (
            <div>
              <Label htmlFor="staff-role">{t("auth.role_label")}</Label>
              <Select value={staffRole} onValueChange={setStaffRole}>
                <SelectTrigger id="staff-role" data-testid="input-staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general_manager">{t("auth.role_general_manager")}</SelectItem>
                  <SelectItem value="operations_manager">{t("auth.role_operations_manager")}</SelectItem>
                  <SelectItem value="manager">{t("auth.role_manager")}</SelectItem>
                  <SelectItem value="operatore">{t("auth.role_operator")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {t("auth.role_help")}
              </p>
            </div>
          )}
          <div>
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" data-testid="input-email" value={email} onChange={(e)=>setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            {/* `minLength` e' uguale a quello che il backend pretende davvero,
                altrimenti il browser lascia passare una password che il server
                rifiuta solo DOPO l'invio: l'utente la riscrive, e non capisce
                se il problema sia la password o l'email del ruolo. Vedi
                `register_staff` in backend/routers/auth.py. */}
            <Input id="password" type="password" data-testid="input-password" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={mode === "staff" ? 8 : 6}/>
            {mode === "staff" && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="auth-staff-password-hint">
                {t("auth.staff_password_hint")}
              </p>
            )}
          </div>
          {mode === "register" && (
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="aff-consent-row">
                <input
                  type="checkbox"
                  checked={affAccepted}
                  onChange={(e) => setAffAccepted(e.target.checked)}
                  required
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#C55A3C]"
                  data-testid="input-aff-consent"
                />
                <span className="text-sm leading-relaxed">
                  {t("auth.aff_consent")}{" "}
                  <a
                    href="/regolamento-affiliazione"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-0.5"
                    data-testid="aff-rules-link"
                  >
                    {t("auth.aff_rules")}
                    <ExternalLink className="w-3 h-3"/>
                  </a>
                  <span className="text-destructive">*</span>
                </span>
              </label>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{t("auth.aff_consent_hint")}</p>
              {/* Il contratto integrale deve essere raggiungibile *prima* del
                  consenso, non promesso e basta. */}
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/legal/contract.docx`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary underline underline-offset-2"
                data-testid="aff-contract-link"
              >
                <ExternalLink className="w-3 h-3"/>
                {t("auth.contract_docx")}
              </a>
              {/* Art. 13 GDPR: l'informativa va data quando si raccolgono i
                  dati, cioè qui, non solo nel footer del sito. Non è un
                  consenso da spuntare: è l'informazione che rende valido
                  quello che l'iscrizione già implica. */}
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground" data-testid="auth-privacy-note">
                {lang === "it"
                  ? "I dati richiesti servono a creare l'account e a eseguire il contratto: senza non possiamo registrare la struttura. Come li trattiamo: "
                  : "The details requested are needed to create the account and perform the contract: without them we cannot register the property. How we process them: "}
                <a
                  href={marketingHref("privacy", lang)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary underline underline-offset-2"
                  data-testid="auth-privacy-link"
                >
                  {lang === "it" ? "informativa privacy" : "privacy notice"}
                </a>
                {lang === "it" ? "." : "."}
              </p>
            </div>
          )}
          <Button type="submit" disabled={loading || (mode === "register" && !affAccepted)} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="auth-submit">
            {mode === "login" ? t("auth.submit_login")
              : mode === "staff"
              ? t("auth.staff_submit")
              : t("auth.submit_register")}
          </Button>

          {/* Social login — Google (web redirect), Apple (native or JS) */}
          <div className="pt-2 space-y-2">
            <button
              type="button"
              onClick={googleLogin}
              className="w-full border border-border rounded-lg py-2.5 px-4 flex items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-all"
              data-testid="auth-google-btn"
            >
              <Chrome className="w-4 h-4"/>
              <span className="text-sm">{t("auth.google")}</span>
            </button>
            {/* Apple: spento finché il Services ID non è configurato (vedi
                `APPLE_SIGNIN_ENABLED`). Il pulsante resta al suo posto, in
                grigio: toglierlo lo farebbe ricomparire un giorno come una
                funzione nuova, invece di una che era solo in attesa. */}
            <button
              type="button"
              onClick={appleLogin}
              disabled={!appleAvailable}
              aria-disabled={!appleAvailable}
              title={!appleAvailable ? t("auth.soon") : undefined}
              className={`w-full rounded-lg py-2.5 px-4 flex items-center justify-center gap-2 transition-all ${
                appleAvailable
                  ? "bg-black text-white hover:bg-black/85"
                  : "cursor-not-allowed border border-border bg-muted text-muted-foreground"
              }`}
              data-testid="auth-apple-btn"
            >
              <Apple className={`w-4 h-4 ${appleAvailable ? "" : "opacity-50"}`}/>
              <span className="text-sm">{t("auth.apple")}</span>
              {!appleAvailable && (
                <span className="ml-1 rounded-full bg-background/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="auth-apple-soon">
                  {t("auth.soon")}
                </span>
              )}
            </button>
          </div>
        </form>

        {mode === "login" && (
          <Link
            to="/forgot-password"
            className="mt-4 text-sm text-muted-foreground hover:text-primary transition-colors block text-center"
            data-testid="auth-forgot-link"
          >
            {t("auth.forgot")}
          </Link>
        )}

        {/* Quanto costa, e quanto si puo' abbassare. */}
        {mode === "register" && (
          <div className="mt-8 pt-8 border-t border-border" data-testid="auth-commissioni">
            <h2 className="font-display text-xl tracking-tight mb-1">
              {t("auth.costs_title")}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t("auth.costs_body")}
            </p>
            <CommissionTable variant="compact"/>
          </div>
        )}

        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-6 text-sm text-muted-foreground hover:text-primary transition-colors block mx-auto"
          data-testid="auth-switch"
        >
          {mode === "login" ? t("auth.switch_to_register") : t("auth.switch_to_login")}
        </button>

        {/* Come arrivare al percorso staff. Senza questo link, chi riceve
            l'invito e apre l'app dal telefono vede il form che chiede il CIN
            della propria struttura — che non ha, perché non è il titolare — e
            lo lascia. Il link c'è anche nella mail, ma la mail è su un altro
            dispositivo. */}
        {mode === "staff" ? (
          <button
            onClick={() => setMode("login")}
            className="mt-4 text-sm text-muted-foreground hover:text-primary transition-colors block mx-auto"
            data-testid="auth-staff-to-login"
          >
            {t("auth.switch_to_login")}
          </button>
        ) : (
          <button
            onClick={() => setMode("staff")}
            className="mt-4 text-xs text-muted-foreground hover:text-primary transition-colors block mx-auto"
            data-testid="auth-to-staff"
          >
            {t("auth.to_staff")}
          </button>
        )}
        </div>

        {/* Pricing preview for hosts */}
        <div className="lg:sticky lg:top-6">
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2">
            {t("auth.host_plans")}
          </div>
          <h2 className="font-display text-2xl mb-4">
            {t("auth.pick_plan")}
          </h2>
          <PricingPreview compact={false} showLink={true}/>
        </div>
      </div>
    </div>
  );
}
